import { type Dictionary } from '@ngrx/entity';

import {
  type CanvasElement,
  findByRefIds,
  type PropsOf,
  WrapperField,
} from '@blaze-runtime/engine-domain';
import {
  type ElementMetaData,
  type FormlikeContainerModelField,
} from '@blaze-shared/elements-domain';
import { type ColumnLike } from '@blaze-shared/schema-domain';

import {
  hasProps,
  hasWrapperRefs,
} from '../../../canvas-elements/element-model/element-props-guards';
import { type FormElement } from '../../../elements/form-container/form-container.model';

/** What validation needs to know about one field of a formlike container's model. */
export interface FormValidationModelField {
  visible: boolean;
  dynamicallyRequired: boolean;
  dynamicallyInvalid: string | undefined;
}

/** The validation view of a formlike container's model, keyed by column name. */
export type FormValidationModel = Record<string, FormValidationModelField>;

/** Only forms that write data are validated; a view-only form has nothing to reject. */
export function shouldFormBeValidated(form: FormElement | undefined): boolean {
  return ['update', 'insert', 'prompt'].includes(form?.props?.content?.action ?? '');
}

/**
 * Projects a formlike container's model onto the flags validation acts on: whether the
 * field is shown at all, plus the required/invalid states its element picked up at runtime
 * (conditions, formulas) rather than from the schema.
 *
 * @param formElement the formlike container whose model is projected
 * @param elementMeta the dynamic field state, for the forms that have any
 * @param getColumnName resolves a model field's column name as the schema knows it today
 */
export function buildFormValidationModel(
  formElement: CanvasElement | undefined,
  elementMeta: Dictionary<ElementMetaData> | undefined,
  getColumnName: (column: ColumnLike) => string | undefined,
): FormValidationModel | undefined {
  if (!hasProps(formElement)) {
    return undefined;
  }

  const model = (formElement.props as PropsOf<'formContainer'> | PropsOf<'editableList'>)?.content
    ?.model as FormlikeContainerModelField[] | undefined;
  const wrapperRefs = hasWrapperRefs(formElement) ? formElement.props.wrapper.refs : undefined;

  return model?.reduce((result, field) => {
    const columnName = getColumnName({ id: field.fieldId, name: field.field });
    if (!columnName) {
      return result;
    }

    const fieldElementId = elementMeta
      ? findByRefIds(wrapperRefs, WrapperField.getRefIds(field.fieldId, field.field))
      : undefined;
    const fieldUiFeatures = fieldElementId ? elementMeta?.[fieldElementId]?.uiFeatures : undefined;

    result[columnName] = {
      visible: field.visible !== false,
      dynamicallyRequired:
        fieldUiFeatures && 'isRequired' in fieldUiFeatures ? fieldUiFeatures.isRequired : false,
      dynamicallyInvalid:
        fieldUiFeatures && 'isInvalid' in fieldUiFeatures ? fieldUiFeatures.isInvalid : undefined,
    };
    return result;
  }, {} as FormValidationModel);
}
